import { Airplane, IconProps, Plus, Swap } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import useFlightBookings from "../../hooks/useFlightBookings";
import useIntlFlightFeatureFlags from "../../hooks/useIntlFlightFeatureFlags";
import { HashLoader } from "react-spinners";
import BookedFlightsList from "../../components/BookedFlightsList";

type Option = {
  key: string;
  title: string;
  desc: string;
  icon: React.ForwardRefExoticComponent<
    IconProps & React.RefAttributes<SVGSVGElement>
  >;
  color: string;
  iconColor: string;
  isEnabled: boolean;
  path: string;
};
const options: Option[] = [
  {
    key: "new-local-flight",
    title: "New Local Flight",
    desc: "Use your Visaro wallet or card",
    icon: Plus,
    color: "visaro-visaMintGreen",
    path: "/aviation/new-local-flight",
    iconColor: "black",
    isEnabled: true,
  },
  {
    key: "flights-bnpl",
    title: "Local flight (BNPL)",
    desc: "Split your flight cost into installments with Visaro",
    icon: Plus,
    color: "visaro-primary-700",
    path: "/aviation/flights",
    iconColor: "white",
    isEnabled: true,
  },
  {
    key: "swap-tickets",
    title: "Swap Tickets (Soon)",
    desc: "Change existing tickets",
    icon: Swap,
    color: "visaro-orange",
    iconColor: "white",
    isEnabled: false,
    path: "/aviation/swap-tickets",
  },
];

const AviationHome = () => {
  const navigate = useNavigate();
  const { fetchingFlightBookings, bookedFlights } = useFlightBookings();
  const { flightsEnabled } = useIntlFlightFeatureFlags();

  const allOptions: Option[] = [
    options[0],
    options[1],
    {
      key: "new-international-flight",
      title: flightsEnabled ? "International Flight" : "International Flight (Soon)",
      desc: "Book upcoming international flights",
      icon: Airplane,
      color: "visaro-primary-700",
      iconColor: "white",
      isEnabled: flightsEnabled,
      path: "/aviation/international/v2/search",
    },
    options[2],
  ];

  return (
    <div className="p-10 px-5 md:px-10 flex flex-col gap-10">
      <div className="flex justify-between gap-5">
        <div className="flex flex-col gap-2">
          <h1 className="text-4xl font-medium">
            Book Your Flight
          </h1>
          <div className="text-gray-500">
            Search and book your flight. Pay in full or spread your payment
            over time.
          </div>
        </div>
        <Link
          to="/aviation/your-flights"
          className="text-visaro-primary-700 font-medium"
        >
          Your Tickets
        </Link>
      </div>
      <div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
          {allOptions.map((option) => {
            return (
              <button
                type="button"
                disabled={!option.isEnabled}
                key={option.key}
                onClick={() => {
                  navigate(option.path);
                }}
                className={`flex flex-col gap-4 items-center justify-center border rounded-md aspect-video hover:bg-gray-100 transition-all cursor-pointer`}
              >
                <div className={`bg-${option.color} p-2 md:p-5 rounded-full`}>
                  <option.icon
                    weight="bold"
                    color={option.iconColor}
                    className="size-4 md:size-6"
                  />
                </div>
                <div className="flex flex-col items-start md:items-center">
                  <div className="font-medium text-lg">{option.title}</div>
                  <div className="text-gray-500 text-left md:text-center">
                    {option.desc}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        {fetchingFlightBookings ? (
          <div className="grid place-items-center gap-2">
            <HashLoader size={40} color="#1A1A31" />
            <div className="animate-pulse text-deepBlue font-medium">Loading your booked flights</div>
          </div>
        ) : (
          <BookedFlightsList bookedFlights={bookedFlights || []} />
        )}
      </div>
    </div>
  );
};

export default AviationHome;
